import * as React from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Checkbox,
  Tooltip,
  makeStyles,
  Theme,
  PrimaryButton,
  DefaultButton,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
  IconButton,
  SortableTable,
  SortableTableHeaderCell,
  TextField,
  FormHelperText,
} from '@bb-ui/react-library';
import { ExpansionPanel } from '@bb-ui/react-library/dist/components/ExpansionPanel';
import { ExpansionPanelSummary } from '@bb-ui/react-library/dist/components/ExpansionPanelSummary';
import { ExpansionPanelDetails } from '@bb-ui/react-library/dist/components/ExpansionPanelDetails';
import { useTranslation } from 'react-i18next';
import orderBy from 'lodash/orderBy';
import { Trash, ChevronLeft, ChevronRight } from '@bb-ui/icons/dist/small';
import { formatGmtInZone } from '../utils';
import { useSiteFiltering, SiteResponse } from '../hooks/useSiteFiltering';
import { useSiteSelection } from '../hooks/useSiteSelection';
import { useDowntimeConflictValidation } from '../hooks/useDowntimeConflictValidation';
import SiteConsistencyErrorDialog from './SiteConsistencyErrorDialog';
import BulkSearchDialog from './BulkSearchDialog';
import SearchResultsSummaryDialog from './SearchResultsSummaryDialog';

const MAX_SELECTION = 60;

const useStyles = makeStyles((theme: Theme) => ({
  dialogContent: {
    '&&.MuiDialogContent-root': {
      padding: 0,
      overflow: 'hidden',
      maxHeight: '75vh',
    },
  },
  modalContent: {
    display: 'flex',
    height: '70vh',
    maxHeight: '70vh',
  },
  leftPanel: {
    width: '20%',
    padding: theme.spacing(2),
    borderRight: `1px solid ${theme.palette.grey[300]}`,
    backgroundColor: theme.palette.background.default,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    overflow: 'hidden',
  },
  topInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexShrink: 0,
  },
  selectedCount: {
    fontWeight: 500,
    fontSize: '0.95rem',
  },
  selectedListContainer: {
    marginTop: theme.spacing(2),
    overflowY: 'auto',
    flex: 1,
  },
  rightPanel: {
    flex: 1,
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  filterContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    flexShrink: 0,
  },
  filterField: {
    flex: '1 1 0',
    minWidth: 150,
  },
  filterActions: {
    display: 'flex',
    justifyContent: 'flex-start',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  filterActionsContainer: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(1),
    flexShrink: 0,
  },
  helperText: {
    marginTop: theme.spacing(1),
    color: theme.palette.error.main,
  },
  tableWrapper: {
    flex: 1,
    overflowY: 'auto',
    minHeight: 0,
  },
  row: {
    height: 48,
    cursor: 'pointer',
  },
  cellText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  tableHeaderRow: {
    backgroundColor: theme.palette.grey[100],
    fontWeight: 600,
  },
  paginationContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: theme.spacing(2),
    padding: theme.spacing(1, 0),
    borderTop: `1px solid ${theme.palette.grey[300]}`,
    flexShrink: 0,
  },
  paginationInfo: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  },
  paginationControls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  pageButton: {
    minWidth: 32,
    height: 32,
    padding: theme.spacing(0.5, 1),
    fontSize: '0.875rem',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    color: theme.palette.text.primary,
    fontWeight: 400,
    '&:hover': {
      backgroundColor: theme.palette.grey[100],
    },
    '&:disabled': {
      cursor: 'default',
    },
  },
  pageButtonActive: {
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  navIconButton: {
    padding: theme.spacing(0.5),
  },
  bulkSearchSeparator: {
    display: 'flex',
    alignItems: 'center',
    margin: theme.spacing(2, 0, 1, 0),
    '&::before, &::after': {
      content: '""',
      flex: 1,
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    '&::before': { marginRight: theme.spacing(1) },
    '&::after': { marginLeft: theme.spacing(1) },
  },
  bulkSearchAction: {
    marginBottom: theme.spacing(1),
  },
  conflictRow: {
    opacity: 0.6,
  },
}));

export interface FilterSitesDialogProps {
  open: boolean;
  onClose: () => void;
  selectedSiteIds: Set<string>;
  onToggleSite: (siteId: string, siteData?: SiteResponse) => void;
  onConfirm?: (selectedSiteIds: Set<string>) => void;
  requiredFleetId?: string;
  requiredAccountId?: string;
  cohortDowntimeDate?: string | null;
  timeZone?: string;
}

const FilterSitesDialog: React.FC<FilterSitesDialogProps> = ({
  open,
  onClose,
  selectedSiteIds,
  onToggleSite,
  onConfirm,
  requiredFleetId,
  requiredAccountId,
  cohortDowntimeDate,
  timeZone,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [expandedPanel, setExpandedPanel] = React.useState<string | false>(false);
  const [sortColumn, setSortColumn] = React.useState<keyof SiteResponse>('captain_id');
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('asc');
  const [bulkSearchDialogOpen, setBulkSearchDialogOpen] = React.useState(false);
  const [bulkSearchResultsDialogOpen, setBulkSearchResultsDialogOpen] = React.useState(false);
  const [bulkSearchResults, setBulkSearchResults] = React.useState<{
    searchedCount: number;
    validCandidates: SiteResponse[];
    sitesInCohort: Array<{
      captain_id: string;
      cohort: string;
      cohort_name: string;
      learn_url?: string;
    }>;
    sitesNotFound: string[];
    sitesNotCandidate: Array<{ captain_id: string; reason: string; learn_url?: string }>;
  } | null>(null);

  const selectedSitesCache = React.useRef<Map<string, SiteResponse>>(new Map());

  const { validateSiteAgainstDowntime } = useDowntimeConflictValidation();

  const {
    filterInputs,
    sites,
    allLoadedSites,
    isLoading,
    handleFilterInputChange,
    applyFilters,
    clearFilters,
    shouldDisableApply,
    getFilterValidationMessage,
    currentPage,
    visitedPages,
    hasMorePages,
    hasPreviousPage,
    goToPage,
    goToNextPage,
    goToPreviousPage,
    getPaginationInfo,
    bulkSearchByCaptainIds,
    processBulkSearchResults,
    applyBulkSearchResults,
    isLoadingBulkSearch,
  } = useSiteFiltering({
    isDialogOpen: open,
  });

  React.useEffect(() => {
    allLoadedSites.forEach((site) => {
      if (!selectedSitesCache.current.has(site.captain_id)) {
        selectedSitesCache.current.set(site.captain_id, site);
      }
    });
  }, [allLoadedSites]);

  const {
    selectedSiteIds: tempSelectedSiteIds,
    siteConsistencyError,
    toggleSite,
    handleSelectAll,
    confirmSelection,
    cancelSelection,
    clearSiteConsistencyError,
    isSelected,
    canSelect,
    selectedCount,
    maxSelection,
  } = useSiteSelection({
    maxSelection: MAX_SELECTION,
    initialSelectedIds: selectedSiteIds,
    isDialogOpen: open,
    allLoadedSites,
    requiredFleetId,
    requiredAccountId,
    sitesCache: selectedSitesCache.current,
  });

  const getSelectionSummary = React.useCallback(
    () => t('cohorts.selectionSummary', { selectedCount, maxSelection }),
    [selectedCount, maxSelection, t],
  );

  const getAllSelectionState = React.useCallback(
    (availableSites: SiteResponse[]) => {
      const availableCount = Math.min(availableSites.length, maxSelection);
      const selectedAvailableCount = availableSites.filter((site) =>
        tempSelectedSiteIds.has(site.captain_id),
      ).length;

      const allSelected = availableCount > 0 && selectedAvailableCount === availableCount;
      const someSelected = selectedAvailableCount > 0 && selectedAvailableCount < availableCount;

      return {
        allSelected,
        someSelected,
        indeterminate: someSelected,
      };
    },
    [tempSelectedSiteIds, maxSelection],
  );

  const hasDowntimeConflict = React.useCallback(
    (site: SiteResponse): boolean => {
      if (!cohortDowntimeDate) return false;
      return !validateSiteAgainstDowntime(cohortDowntimeDate, site);
    },
    [cohortDowntimeDate, validateSiteAgainstDowntime],
  );

  const getDisabledTooltip = React.useCallback(
    (site: SiteResponse) => {
      if (isSelected(site.captain_id)) {
        return '';
      }
      if (hasDowntimeConflict(site)) {
        return t('cohorts.validation.siteDowntimeConflictTooltip');
      }
      if (selectedCount >= maxSelection) {
        return t('cohorts.maxSitesSelectionTooltip', { maxSelection });
      }
      return '';
    },
    [isSelected, hasDowntimeConflict, selectedCount, maxSelection, t],
  );

  const sortedSites = React.useMemo(
    () => orderBy(sites, [sortColumn], [sortDirection]),
    [sites, sortColumn, sortDirection],
  );

  const anyRowHasDifferentDates = React.useMemo(
    () =>
      sortedSites.some(
        (s) =>
          s?.notifications?.start_suggested_date &&
          s?.notifications?.end_suggested_date &&
          s.notifications.start_suggested_date !== s.notifications.end_suggested_date,
      ),
    [sortedSites],
  );

  const selectableSites = React.useMemo(
    () => (cohortDowntimeDate ? sites.filter((site) => !hasDowntimeConflict(site)) : sites),
    [sites, cohortDowntimeDate, hasDowntimeConflict],
  );

  const selectedSitesDetails = React.useMemo(() => {
    const details = Array.from(tempSelectedSiteIds)
      .map((id) => {
        const loadedSite = allLoadedSites.find((s) => s.captain_id === id);
        if (loadedSite) {
          selectedSitesCache.current.set(id, loadedSite);
          return loadedSite;
        }

        const cachedSite = selectedSitesCache.current.get(id);
        if (cachedSite) return cachedSite;

        return null;
      })
      .filter((s): s is SiteResponse => !!s);
    return details;
  }, [allLoadedSites, tempSelectedSiteIds]);

  const handleSortChanged = (params: { sortColumnId?: string; sortDirection?: 'asc' | 'desc' }) => {
    if (!params.sortColumnId || !params.sortDirection) return false;
    setSortColumn(params.sortColumnId as keyof SiteResponse);
    setSortDirection(params.sortDirection);
    return { columnId: params.sortColumnId, sortDirection: params.sortDirection };
  };

  const handleRowClick = (site: SiteResponse) => {
    if (hasDowntimeConflict(site)) return;
    if (canSelect(site.captain_id) || isSelected(site.captain_id)) {
      toggleSite(site.captain_id);
    }
  };

  const handleCheckboxClick = (e: React.MouseEvent, site: SiteResponse) => {
    e.stopPropagation();
    handleRowClick(site);
  };

  const handleSelectAllChange = (checked: boolean) => {
    handleSelectAll(selectableSites, checked);
  };

  const handleCancel = React.useCallback(() => {
    cancelSelection();
    onClose();
  }, [cancelSelection, onClose]);

  const commitSelection = React.useCallback(() => {
    const sitesToRemove = Array.from(selectedSiteIds).filter((id) => !tempSelectedSiteIds.has(id));
    const sitesToAdd = Array.from(tempSelectedSiteIds).filter((id) => !selectedSiteIds.has(id));

    sitesToRemove.forEach((siteId) => {
      onToggleSite(siteId);
    });

    sitesToAdd.forEach((siteId) => {
      const site =
        selectedSitesCache.current.get(siteId) ||
        allLoadedSites.find((s) => s.captain_id === siteId);
      if (site) {
        onToggleSite(siteId, site);
      }
    });

    if (onConfirm) {
      onConfirm(new Set(tempSelectedSiteIds));
    }

    onClose();
  }, [selectedSiteIds, tempSelectedSiteIds, allLoadedSites, onToggleSite, onConfirm, onClose]);

  const handleDone = React.useCallback(() => {
    const isValid = confirmSelection();
    if (!isValid) {
      return;
    }

    commitSelection();
  }, [confirmSelection, commitSelection]);

  const handleRemoveIncompatibleSites = React.useCallback(() => {
    if (siteConsistencyError?.incompatibleSites) {
      siteConsistencyError.incompatibleSites.forEach((siteId) => {
        toggleSite(siteId);
      });
      clearSiteConsistencyError();
    }
  }, [siteConsistencyError, toggleSite, clearSiteConsistencyError]);

  const handleBulkSearchClick = React.useCallback(() => {
    setBulkSearchDialogOpen(true);
  }, []);

  const handleBulkSearchClose = React.useCallback(() => {
    setBulkSearchDialogOpen(false);
  }, []);

  const handleBulkSearch = React.useCallback(
    async (captainIds: string[]) => {
      const response = await bulkSearchByCaptainIds(captainIds);

      if (!response?.data) {
        return;
      }

      const foundSites = response.data.items || [];
      const failures: Array<{ id: string; reason: string }> = response.data.failures || [];

      const notFoundIds = failures.map((failure) => failure.id);

      const { validCandidates, sitesInCohort, sitesNotCandidate } =
        processBulkSearchResults(foundSites);

      setBulkSearchResults({
        searchedCount: captainIds.length,
        validCandidates,
        sitesInCohort,
        sitesNotFound: notFoundIds,
        sitesNotCandidate,
      });

      setBulkSearchDialogOpen(false);
      setBulkSearchResultsDialogOpen(true);
    },
    [bulkSearchByCaptainIds, processBulkSearchResults],
  );

  const handleBulkSearchResultsClose = React.useCallback(() => {
    if (bulkSearchResults && bulkSearchResults.validCandidates.length > 0) {
      applyBulkSearchResults(bulkSearchResults.validCandidates);
    }
    setBulkSearchResultsDialogOpen(false);
    setBulkSearchResults(null);
  }, [bulkSearchResults, applyBulkSearchResults]);

  const getAriaSortMessage = (columnId: string, dir?: 'asc' | 'desc') => {
    const labels: Record<string, string> = {
      captain_id: t('cohorts.captainId'),
      learn_id: t('cohorts.learnId'),
      learn_url: t('cohorts.learnUrl'),
      client_name: t('cohorts.clientName'),
      fleet_name: t('cohorts.siteFleet'),
      aws_account: t('cohorts.awsAccount'),
      account_name: t('cohorts.accountName'),
      tenant_id: t('cohorts.tenantId'),
      preferred_downtime_date: t('cohorts.preferredDowntimeDate'),
    };
    const direction = dir ?? 'none';
    return `${labels[columnId] ?? columnId} sorted ${direction}`;
  };

  const selectAllState = getAllSelectionState(selectableSites);

  const paginationInfo = getPaginationInfo();
  const showPagination = allLoadedSites.length > 0;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCancel}
        maxWidth={false}
        fullWidth
        PaperProps={{
          style: {
            overflow: 'hidden',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle onClose={handleCancel} closeLabel={t('global.close')}>
          {t('cohorts.selectCandidateSites')}
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <div className={classes.modalContent}>
            <div className={classes.leftPanel}>
              <div className={classes.topInfo}>
                <Typography className={classes.selectedCount}>{getSelectionSummary()}</Typography>
              </div>
              <div className={classes.selectedListContainer}>
                {selectedSitesDetails.length === 0 ? (
                  <Typography variant="body1" color="textSecondary">
                    {t('cohorts.noSitesSelected')}
                  </Typography>
                ) : (
                  selectedSitesDetails.map((site, idx) => {
                    const panelId = `selected-site-${idx}`;
                    return (
                      <ExpansionPanel
                        key={site.captain_id}
                        border="thin"
                        expanded={expandedPanel === panelId}
                        onChange={(_, isExpanded) => setExpandedPanel(isExpanded ? panelId : false)}
                      >
                        <ExpansionPanelSummary
                          id={`${panelId}-summary`}
                          aria-controls={`${panelId}-details`}
                        >
                          <Typography variant="h3" component="span">
                            {site.learn_url || site.captain_id}
                          </Typography>
                        </ExpansionPanelSummary>
                        <ExpansionPanelDetails>
                          <div style={{ width: '100%' }}>
                            <Typography variant="body2">
                              <strong>{t('cohorts.captainId')}:</strong> {site.captain_id}
                            </Typography>
                            <Typography variant="body2">
                              <strong>{t('cohorts.learnId')}:</strong> {site.learn_id || '-'}
                            </Typography>
                            <Typography variant="body2">
                              <strong>{t('cohorts.clientName')}:</strong> {site.client_name || '-'}
                            </Typography>
                            <Typography variant="body2">
                              <strong>{t('cohorts.siteFleet')}:</strong> {site.fleet_name || '-'}
                            </Typography>
                            <Typography variant="body2">
                              <strong>{t('cohorts.awsAccount')}:</strong> {site.aws_account || '-'}
                            </Typography>
                            <Typography variant="body2">
                              <strong>{t('cohorts.preferredDowntimeStart')}:</strong>{' '}
                              {formatGmtInZone(
                                site?.notifications?.start_suggested_date,
                                timeZone,
                              ) || '-'}
                            </Typography>
                            {site?.notifications?.start_suggested_date !==
                              site?.notifications?.end_suggested_date && (
                              <Typography variant="body2">
                                <strong>{t('cohorts.preferredDowntimeEnd')}:</strong>{' '}
                                {formatGmtInZone(
                                  site?.notifications?.end_suggested_date,
                                  timeZone,
                                ) || '-'}
                              </Typography>
                            )}
                            <IconButton
                              size="small"
                              onClick={() => toggleSite(site.captain_id)}
                              aria-label={t('cohorts.removeSite')}
                            >
                              <Trash color="error" />
                            </IconButton>
                          </div>
                        </ExpansionPanelDetails>
                      </ExpansionPanel>
                    );
                  })
                )}
              </div>
            </div>

            <div className={classes.rightPanel}>
              <div className={classes.filterContainer}>
                <TextField
                  id="filter-captain-id"
                  label={t('cohorts.captainId')}
                  floatingLabel
                  className={classes.filterField}
                  value={filterInputs.captainId}
                  onChange={(e) => handleFilterInputChange('captainId', e.target.value)}
                />
                <TextField
                  id="filter-learn-url"
                  label={t('cohorts.learnUrl')}
                  floatingLabel
                  className={classes.filterField}
                  value={filterInputs.learnUrl}
                  onChange={(e) => handleFilterInputChange('learnUrl', e.target.value)}
                />
                <TextField
                  id="filter-client-name"
                  label={t('cohorts.clientName')}
                  floatingLabel
                  className={classes.filterField}
                  value={filterInputs.clientName}
                  onChange={(e) => handleFilterInputChange('clientName', e.target.value)}
                />
              </div>

              <div className={classes.filterActionsContainer}>
                <div className={classes.filterActions}>
                  <DefaultButton onClick={clearFilters}>{t('cohorts.clearFilters')}</DefaultButton>
                  <PrimaryButton onClick={applyFilters} disabled={shouldDisableApply}>
                    {t('cohorts.applyFilters')}
                  </PrimaryButton>
                </div>
                {shouldDisableApply && (
                  <FormHelperText className={classes.helperText}>
                    {getFilterValidationMessage()}
                  </FormHelperText>
                )}

                <div className={classes.bulkSearchSeparator}>
                  <Typography variant="body2" color="textSecondary">
                    {t('cohorts.bulkSearch.orSeparator')}
                  </Typography>
                </div>
                <div className={classes.bulkSearchAction}>
                  <DefaultButton onClick={handleBulkSearchClick} fullWidth>
                    {t('cohorts.bulkSearch.button')}
                  </DefaultButton>
                </div>
              </div>

              <div className={classes.tableWrapper}>
                <SortableTable
                  id="sites-table"
                  aria-label={t('cohorts.selectCandidateSites')}
                  onSortChanged={handleSortChanged}
                  getAriaSortMessage={getAriaSortMessage}
                  tableProps={{ role: 'grid' }}
                >
                  <TableBody>
                    <TableRow className={`${classes.row} ${classes.tableHeaderRow}`}>
                      <TableHead id="header-select-all">
                        <Checkbox
                          checked={selectAllState.allSelected}
                          indeterminate={selectAllState.indeterminate}
                          onChange={(e) => handleSelectAllChange(e.target.checked)}
                          disabled={sites.length === 0}
                        />
                      </TableHead>
                      <SortableTableHeaderCell id="captain-id" columnId="captain_id">
                        {t('cohorts.captainId')}
                      </SortableTableHeaderCell>
                      <SortableTableHeaderCell id="header-learn-url" columnId="learn_url">
                        {t('cohorts.learnUrl')}
                      </SortableTableHeaderCell>
                      <SortableTableHeaderCell id="header-client-name" columnId="client_name">
                        {t('cohorts.clientName')}
                      </SortableTableHeaderCell>
                      <SortableTableHeaderCell id="header-site-fleet" columnId="fleet_name">
                        {t('cohorts.siteFleet')}
                      </SortableTableHeaderCell>
                      <SortableTableHeaderCell id="header-tenant-id" columnId="tenant_id">
                        {t('cohorts.tenantId')}
                      </SortableTableHeaderCell>
                      <SortableTableHeaderCell
                        id="header-database-size"
                        columnId="database_rds_size"
                      >
                        {t('cohorts.DatabaseSize')}
                      </SortableTableHeaderCell>
                      <SortableTableHeaderCell id="header-account-name" columnId="account_name">
                        {t('cohorts.accountName')}
                      </SortableTableHeaderCell>
                      {anyRowHasDifferentDates ? (
                        <>
                          <SortableTableHeaderCell
                            id="header-preferred-downtime-start"
                            columnId="preferred_downtime_start"
                          >
                            <Tooltip
                              title={t('cohorts.preferredDowntimeStartTooltip').toString()}
                              arrow
                            >
                              <span>{t('cohorts.preferredDowntimeStart')}</span>
                            </Tooltip>
                          </SortableTableHeaderCell>
                          <SortableTableHeaderCell
                            id="header-preferred-downtime-end"
                            columnId="preferred_downtime_end"
                          >
                            <Tooltip
                              title={t('cohorts.preferredDowntimeEndTooltip').toString()}
                              arrow
                            >
                              <span>{t('cohorts.preferredDowntimeEnd')}</span>
                            </Tooltip>
                          </SortableTableHeaderCell>
                        </>
                      ) : (
                        <SortableTableHeaderCell
                          id="header-preferred-downtime"
                          columnId="preferred_downtime_start"
                        >
                          <Tooltip
                            title={t('cohorts.preferredDowntimeStartTooltip').toString()}
                            arrow
                          >
                            <span>{t('cohorts.preferredDowntime')}</span>
                          </Tooltip>
                        </SortableTableHeaderCell>
                      )}
                    </TableRow>
                    {sortedSites.length === 0 && !isLoading ? (
                      <TableRow>
                        <TableCell
                          colSpan={anyRowHasDifferentDates ? 9 : 8}
                          style={{ textAlign: 'center', padding: '2rem' }}
                        >
                          <Typography variant="body1" color="textSecondary">
                            {t('cohorts.noSitesFound')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedSites.map((site) => {
                        const siteIsSelected = isSelected(site.captain_id);
                        const siteCanSelect = canSelect(site.captain_id);
                        const siteHasConflict = hasDowntimeConflict(site);
                        const disableCheckbox =
                          siteHasConflict || (!siteIsSelected && !siteCanSelect);

                        return (
                          <TableRow
                            key={site.captain_id}
                            className={`${classes.row} ${siteHasConflict ? classes.conflictRow : ''}`}
                            onClick={() => handleRowClick(site)}
                          >
                            <TableCell>
                              <Tooltip title={getDisabledTooltip(site)}>
                                <span>
                                  <Checkbox
                                    checked={siteIsSelected}
                                    disabled={disableCheckbox}
                                    onClick={(e) => handleCheckboxClick(e, site)}
                                    onChange={() => {}}
                                    aria-label={t('cohorts.selectRow')}
                                  />
                                </span>
                              </Tooltip>
                            </TableCell>
                            <TableCell className={classes.cellText}>{site.captain_id}</TableCell>
                            <TableCell className={classes.cellText}>
                              {site.learn_url || '-'}
                            </TableCell>
                            <TableCell className={classes.cellText}>
                              {site.client_name || '-'}
                            </TableCell>
                            <TableCell className={classes.cellText}>
                              {site.fleet_name || '-'}
                            </TableCell>
                            <TableCell className={classes.cellText}>
                              {site.tenant_id || '-'}
                            </TableCell>
                            <TableCell className={classes.cellText}>
                              {site.db_size || '-'}
                            </TableCell>
                            <TableCell className={classes.cellText}>
                              {site.account_name || '-'}
                            </TableCell>
                            <TableCell className={classes.cellText}>
                              {formatGmtInZone(
                                site?.notifications?.start_suggested_date,
                                timeZone,
                              ) || '-'}
                            </TableCell>
                            {anyRowHasDifferentDates && (
                              <TableCell className={classes.cellText}>
                                {site?.notifications?.start_suggested_date ===
                                site?.notifications?.end_suggested_date
                                  ? ''
                                  : formatGmtInZone(
                                      site?.notifications?.end_suggested_date,
                                      timeZone,
                                    ) || '-'}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </SortableTable>
              </div>

              {showPagination && (
                <div className={classes.paginationContainer}>
                  <Typography className={classes.paginationInfo}>
                    {t('cohorts.pagination.showing', {
                      start: paginationInfo.start,
                      end: paginationInfo.end,
                    })}
                    {paginationInfo.hasMore && ` ${t('cohorts.pagination.moreAvailable')}`}
                  </Typography>

                  <div className={classes.paginationControls}>
                    <IconButton
                      className={classes.navIconButton}
                      onClick={goToPreviousPage}
                      disabled={!hasPreviousPage || isLoading}
                      aria-label={t('cohorts.pagination.previousPage')}
                      size="small"
                    >
                      <ChevronLeft />
                    </IconButton>

                    {visitedPages.map((pageIndex) => (
                      <button
                        key={pageIndex}
                        className={`${classes.pageButton} ${
                          currentPage === pageIndex ? classes.pageButtonActive : ''
                        }`}
                        onClick={() => goToPage(pageIndex)}
                        disabled={currentPage === pageIndex || isLoading}
                        aria-label={t('cohorts.pagination.pageNumber', {
                          pageNumber: pageIndex + 1,
                        })}
                        aria-current={currentPage === pageIndex ? 'page' : undefined}
                      >
                        {pageIndex + 1}
                      </button>
                    ))}

                    {hasMorePages && (
                      <Typography variant="body2" style={{ margin: '0 4px', color: '#999' }}>
                        {t('cohorts.pagination.ellipsis')}
                      </Typography>
                    )}

                    <IconButton
                      className={classes.navIconButton}
                      onClick={goToNextPage}
                      disabled={!hasMorePages || isLoading}
                      aria-label={t('cohorts.pagination.nextPage')}
                      size="small"
                    >
                      <ChevronRight />
                    </IconButton>
                  </div>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <DefaultButton onClick={handleCancel}>{t('global.cancel')}</DefaultButton>
          <PrimaryButton onClick={handleDone}>{t('global.done')}</PrimaryButton>
        </DialogActions>
      </Dialog>

      <SiteConsistencyErrorDialog
        open={!!siteConsistencyError}
        onClose={clearSiteConsistencyError}
        onRemoveIncompatible={handleRemoveIncompatibleSites}
        onRemoveSite={toggleSite}
        requiredFleet={siteConsistencyError?.requiredFleet}
        requiredAccount={siteConsistencyError?.requiredAccount}
        incompatibleSites={siteConsistencyError?.incompatibleSites}
        allSelectedSites={selectedSitesDetails}
      />

      <BulkSearchDialog
        open={bulkSearchDialogOpen}
        onClose={handleBulkSearchClose}
        onSearch={handleBulkSearch}
        isLoading={isLoadingBulkSearch}
      />

      {bulkSearchResults && (
        <SearchResultsSummaryDialog
          open={bulkSearchResultsDialogOpen}
          onClose={handleBulkSearchResultsClose}
          searchedCount={bulkSearchResults.searchedCount}
          validCandidates={bulkSearchResults.validCandidates}
          sitesInCohort={bulkSearchResults.sitesInCohort}
          sitesNotFound={bulkSearchResults.sitesNotFound}
          sitesNotCandidate={bulkSearchResults.sitesNotCandidate}
        />
      )}
    </>
  );
};

export default FilterSitesDialog;
